import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  FileText,
  Sparkles,
  UserRound,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../styles/project-details.css";

const SECTION_DEFINITIONS = [
  {
    id: "abstract",
    label: "Abstract",
    eyebrow: "Project overview",
    field: "Abstract",
    kind: "overview",
  },
  {
    id: "introduction",
    label: "Introduction",
    eyebrow: "The opportunity",
    field: "Introduction",
    kind: "prose",
  },
  {
    id: "objectives",
    label: "Objectives",
    eyebrow: "What success required",
    field: "Objectives",
    kind: "objective-grid",
  },
  {
    id: "methodology",
    label: "Methodology",
    eyebrow: "How it was built",
    field: "Methodology",
    kind: "steps",
  },
  {
    id: "features",
    label: "Features",
    eyebrow: "Core capabilities",
    field: "Features",
    kind: "feature-grid",
  },
  {
    id: "challenges",
    label: "Challenges",
    eyebrow: "Constraints addressed",
    field: "Challenges",
    kind: "challenges",
  },
  {
    id: "results-impact",
    label: "Results & Impact",
    eyebrow: "What changed",
    field: "Results_Impact",
    kind: "prose",
  },
  {
    id: "conclusion-future-work",
    label: "Conclusion & Future Work",
    eyebrow: "What comes next",
    field: "Conclusion_Future_Work",
    kind: "prose",
  },
];

const hasContent = (value) => {
  if (Array.isArray(value)) {
    return value.some((item) => String(item || "").trim());
  }

  return Boolean(String(value || "").trim());
};

const cleanList = (value) =>
  Array.isArray(value)
    ? value.map((item) => String(item || "").trim()).filter(Boolean)
    : [];

const splitParagraphs = (value) =>
  String(value || "")
    .replace(/\r\n?/g, "\n")
    .split(/\n\s*\n+/)
    .map((paragraph) => paragraph.replace(/\s*\n\s*/g, " ").trim())
    .filter(Boolean);

const formatProjectDate = (date) => {
  if (!date) return "";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const ProjectState = ({ type, title, message }) => (
  <div
    className={`project-detail-state project-detail-state--${type}`}
    role={type === "error" ? "alert" : "status"}
    aria-live="polite"
  >
    <span className="project-detail-state__icon" aria-hidden="true">
      {type === "loading" ? <span className="project-detail-spinner" /> : <FileText size={24} />}
    </span>
    <h1>{title}</h1>
    <p>{message}</p>
    {type !== "loading" ? (
      <Link to="/projects" className="project-detail-button project-detail-button--secondary">
        <ArrowLeft size={18} aria-hidden="true" />
        Back to projects
      </Link>
    ) : null}
  </div>
);

const ProjectSection = ({ section, sectionNumber }) => {
  const headingId = `${section.id}-heading`;

  return (
    <section
      id={section.id}
      className={`project-detail-section project-detail-section--${section.kind}`}
      aria-labelledby={headingId}
      data-section-kind={section.kind}
    >
      <header className="project-detail-section__header">
        <span className="project-detail-section__number" aria-hidden="true">
          {String(sectionNumber).padStart(2, "0")}
        </span>
        <div>
          <p>{section.eyebrow}</p>
          <h2 id={headingId}>{section.label}</h2>
        </div>
      </header>

      {section.kind === "overview" ? (
        <div className="project-detail-overview">
          <Sparkles size={22} aria-hidden="true" />
          <div className="project-detail-paragraphs">
            {splitParagraphs(section.content).map((paragraph, index) => (
              <p key={`${section.id}-paragraph-${index}`}>{paragraph}</p>
            ))}
          </div>
        </div>
      ) : null}

      {section.kind === "prose" ? (
        <div className="project-detail-prose project-detail-paragraphs">
          {splitParagraphs(section.content).map((paragraph, index) => (
            <p key={`${section.id}-paragraph-${index}`}>{paragraph}</p>
          ))}
        </div>
      ) : null}

      {section.kind === "objective-grid" || section.kind === "feature-grid" ? (
        <ul className="project-detail-card-grid">
          {section.content.map((item, index) => (
            <li key={`${item}-${index}`}>
              <span className="project-detail-card-grid__icon" aria-hidden="true">
                <Check size={17} strokeWidth={2.7} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {section.kind === "steps" ? (
        <ol className="project-detail-steps">
          {section.content.map((item, index) => (
            <li key={`${item}-${index}`}>
              <span className="project-detail-steps__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <p>{item}</p>
            </li>
          ))}
        </ol>
      ) : null}

      {section.kind === "challenges" ? (
        <ul className="project-detail-challenges">
          {section.content.map((item, index) => (
            <li key={`${item}-${index}`}>
              <AlertTriangle size={18} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};

const ProjectDetails = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("");

  const availableSections = useMemo(() => {
    if (!project?.sections) return [];

    return SECTION_DEFINITIONS.reduce((sections, definition) => {
      const rawContent = project.sections[definition.field];
      if (!hasContent(rawContent)) return sections;

      sections.push({
        ...definition,
        content: Array.isArray(rawContent) ? cleanList(rawContent) : String(rawContent).trim(),
      });
      return sections;
    }, []);
  }, [project]);

  useEffect(() => {
    let isActive = true;

    const fetchProject = async () => {
      setLoading(true);
      setError(null);
      setProject(null);

      try {
        const response = await axios.get(
          `${API_ENDPOINTS.getOneArticle}/${projectId}`
        );

        if (!isActive) return;

        if (response.status === 200) {
          setProject(response.data.article);
        } else {
          setError("Project not found.");
        }
      } catch (requestError) {
        console.error("Error fetching project details:", requestError);
        if (isActive) {
          setError("Failed to load project details. Please try again later.");
        }
      } finally {
        if (isActive) setLoading(false);
      }
    };

    fetchProject();

    return () => {
      isActive = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (!availableSections.length) {
      setActiveSection("");
      return undefined;
    }

    setActiveSection((current) =>
      availableSections.some((section) => section.id === current)
        ? current
        : availableSections[0].id
    );

    if (typeof IntersectionObserver === "undefined") return undefined;

    const sectionElements = availableSections
      .map((section) => document.getElementById(section.id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visibleEntry) setActiveSection(visibleEntry.target.id);
      },
      {
        rootMargin: "-18% 0px -66% 0px",
        threshold: [0, 0.15, 0.4, 0.75],
      }
    );

    sectionElements.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [availableSections]);

  if (loading) {
    return (
      <ProjectState
        type="loading"
        title="Loading project"
        message="Preparing the full case study…"
      />
    );
  }

  if (error) {
    return <ProjectState type="error" title="We couldn’t load this project" message={error} />;
  }

  if (!project) {
    return (
      <ProjectState
        type="empty"
        title="Project unavailable"
        message="No project data is available for this page."
      />
    );
  }

  const formattedDate = formatProjectDate(project.date);
  const metaDescription =
    project.sections?.Introduction?.slice(0, 180) ||
    project.subtitle ||
    "Project from Mohamed Gad covering AI, operations, and venture-building.";
  // Force social previews to use personal headshot instead of per-project images
  const metaImage = "https://www.mohamedgad.com/mohamed-gad.png";
  const metaUrl =
    typeof window !== "undefined"
      ? window.location.href
      : `https://mohamedgad.com/projects/${projectId || ""}`;

  return (
    <article className="project-detail-page">
      <Helmet>
        <title>{project.title || "Project"} | Mohamed Gad</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={project.title || "Project"} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:url" content={metaUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={project.title || "Project"} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={metaImage} />
      </Helmet>

      <Link to="/projects" className="project-detail-back">
        <ArrowLeft size={18} aria-hidden="true" />
        Back to projects
      </Link>

      <header className="project-detail-hero">
        <div className="project-detail-hero__copy">
          <span className="project-detail-category">
            {project.category || "Project case study"}
          </span>
          <h1>{project.title}</h1>
          {project.subtitle ? <p className="project-detail-hero__subtitle">{project.subtitle}</p> : null}

          {project.publishedBy || formattedDate ? (
            <div className="project-detail-meta" aria-label="Project publication details">
              {project.publishedBy ? (
                <span>
                  <UserRound size={17} aria-hidden="true" />
                  <span>
                    By <strong>{project.publishedBy}</strong>
                  </span>
                </span>
              ) : null}
              {formattedDate ? (
                <span>
                  <CalendarDays size={17} aria-hidden="true" />
                  <time dateTime={project.date}>{formattedDate}</time>
                </span>
              ) : null}
            </div>
          ) : null}

          {project.demoLink ? (
            <a
              href={project.demoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="project-detail-button project-detail-button--primary"
            >
              View live demo
              <ArrowUpRight size={18} aria-hidden="true" />
            </a>
          ) : null}
        </div>

        <figure className="project-detail-hero__media">
          {project.image ? (
            <img src={project.image} alt={project.title} loading="eager" decoding="async" />
          ) : (
            <div className="project-detail-hero__placeholder" aria-label="Project image unavailable">
              <Sparkles size={44} aria-hidden="true" />
              <span>Project case study</span>
            </div>
          )}
        </figure>
      </header>

      <div
        className={`project-detail-layout ${
          availableSections.length ? "" : "project-detail-layout--without-nav"
        }`}
      >
        {availableSections.length ? (
          <aside className="project-detail-sidebar">
            <nav className="project-detail-nav" aria-label="Project sections">
              <p>On this page</p>
              <ol>
                {availableSections.map((section, index) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className={activeSection === section.id ? "is-active" : ""}
                      aria-current={activeSection === section.id ? "location" : undefined}
                    >
                      <span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                      {section.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>
        ) : null}

        <div className="project-detail-content">
          {availableSections.length ? (
            availableSections.map((section, index) => (
              <ProjectSection key={section.id} section={section} sectionNumber={index + 1} />
            ))
          ) : (
            <div className="project-detail-content__empty" role="status">
              <FileText size={24} aria-hidden="true" />
              <h2>Case-study details are coming soon</h2>
              <p>This project does not have any published sections yet.</p>
            </div>
          )}

          {project.demoLink ? (
            <aside className="project-detail-cta" aria-label="Project demonstration">
              <div>
                <p>See it in action</p>
                <h2>Explore the working project</h2>
                <span>Open the live experience in a new tab and see how the ideas come together.</span>
              </div>
              <a
                href={project.demoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="project-detail-button project-detail-button--light"
              >
                View live demo
                <ArrowUpRight size={18} aria-hidden="true" />
              </a>
            </aside>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default ProjectDetails;
